// CrossingTheChasmVisualizer.jsx
// Adaptive Product — Strategy Intelligence Platform
// Crossing the Chasm Market Opportunity Visualization
// Drop into dva-frontend-vite/src/components/ or pages/

import { useState, useEffect } from "react";

// ─── CONFIG — edit here without touching layout ─────────────────────────────
const CONFIG = {
  currentPositionPercent: 28,           // 0–100 along the curve X axis
  stageLabel: "Pre-Chasm · Mid–Late Early Adopter",
  wedgeLabel: "Capital Allocation / Portfolio Prioritization / ROI Governance",
  shortTermStrategyLabel: "Outcome-Driven Structured Engagements",
  probabilityMarketPenetration: { low: 60, high: 75 },
  probabilityCategoryLeadership: { low: 30, high: 40 },
  probabilityDominance: { low: 15, high: 25 },
  milestones: [
    { num: "01", label: "Lock Wedge Positioning", detail: "Crystallize category language around portfolio ROI governance" },
    { num: "02", label: "Secure Lighthouse Clients", detail: "3–5 $2B–$15B health systems with named proof points" },
    { num: "03", label: "Produce CFO-Grade Evidence", detail: "Quantified ROI cases that survive board-level scrutiny" },
    { num: "04", label: "Standardize Whole-Product", detail: "Repeatable delivery model, onboarding, and success metrics" },
    { num: "05", label: "Establish Category Language", detail: "SIP / Decision Infrastructure becomes industry vocabulary" },
  ],
  chasmStartPercent: 33,
  chasmEndPercent: 42,
};

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
const T = {
  navy:    "#18365E",
  navyMid: "#1E4272",
  navyLight: "#2A5A9F",
  coral:   "#FF6E4C",
  coralLight: "#FF8A6D",
  bg:      "#F5F7FA",
  bgDark:  "#EEF1F6",
  border:  "#e5e7eb",
  muted:   "#717182",
  white:   "#FFFFFF",
  gold:    "#E8A838",
};

// ─── SVG CURVE MATH ──────────────────────────────────────────────────────────
// Normal-distribution-style bell for the adoption curve
function gaussY(x, mu, sigma, scale) {
  return scale * Math.exp(-0.5 * Math.pow((x - mu) / sigma, 2));
}

function buildCurvePath(W, H, padX, padTop, padBottom) {
  const pts = [];
  const steps = 200;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const x = padX + t * (W - 2 * padX);
    // Shift the bell right so innovators are small, early majority is peak
    const norm = gaussY(t, 0.62, 0.18, 1);
    const y = padTop + (H - padTop - padBottom) * (1 - norm * 0.88);
    pts.push(`${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`);
  }
  return pts.join(" ");
}

// Map a 0–100 percent to SVG X coordinate
function pctToX(pct, W, padX) {
  return padX + (pct / 100) * (W - 2 * padX);
}

// ─── INTERPRETATION PANELS ───────────────────────────────────────────────────
const MARKET_CARDS = [
  {
    id: "A",
    title: "Current Position",
    color: T.coral,
    items: [
      "Problem validated across health systems",
      "No standardized solution category yet",
      "Real competition is spreadsheets + committees",
      "Mid–Late Early Adopter cohort is reachable now",
    ],
  },
  {
    id: "B",
    title: "Why This Market Exists Now",
    color: T.navyLight,
    items: [
      "Structural margin compression on health systems",
      "Unmanageable volume of analytics & AI initiatives",
      "CFO demand for faster, provable ROI",
      "Governance and decision-making are the bottleneck",
    ],
  },
  {
    id: "C",
    title: "What Blocks Mainstream Adoption",
    color: T.gold,
    items: [
      "Trust gap — no proven category leader",
      "Organizational inertia and political resistance",
      "No standard budget line item for Decision Infrastructure",
      "Whole-product delivery not yet standardized",
    ],
  },
];

const MONETIZATION_CARDS = [
  {
    id: "D",
    title: "Short-Term Monetization",
    color: T.coral,
    desc: "Pre-chasm markets do not reliably fund pure SaaS. Outcome-driven structured engagements fund category creation — they are not a distraction.",
    items: [
      "Strategy Intelligence Assessment",
      "Strategy Intelligence Sprint",
      "Portfolio Activation Engagement",
      "Decision Framework Implementation",
    ],
  },
  {
    id: "E",
    title: "Why Services Are Strategic",
    color: T.navyLight,
    desc: "Each engagement is simultaneously a trust-builder, proof generator, cash flow engine, and platform on-ramp.",
    items: [
      "Builds credibility before category exists",
      "Produces the CFO-grade evidence the market needs",
      "Generates cash flow while platform matures",
      "Converts to recurring platform revenue post-proof",
    ],
  },
];

// ─── SUB-COMPONENTS ──────────────────────────────────────────────────────────

function ProbabilityBar({ label, low, high, color }) {
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 300);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{ marginBottom: "14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
        <span style={{ fontSize: "11px", fontWeight: 600, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</span>
        <span style={{ fontSize: "13px", fontWeight: 700, color: T.navy }}>{low}–{high}%</span>
      </div>
      <div style={{ height: "6px", background: T.bgDark, borderRadius: "99px", overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: animated ? `${high}%` : "0%",
            background: `linear-gradient(90deg, ${color}99, ${color})`,
            borderRadius: "99px",
            transition: "width 1.1s cubic-bezier(0.22, 1, 0.36, 1)",
            position: "relative",
          }}
        >
          <div style={{
            position: "absolute", right: 0, top: "-2px", width: "10px", height: "10px",
            background: color, borderRadius: "50%", border: `2px solid white`,
            boxShadow: `0 0 0 2px ${color}44`,
          }} />
        </div>
      </div>
    </div>
  );
}

function InfoCard({ card, index }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100 + index * 120);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <div style={{
      background: T.white,
      border: `1px solid ${T.border}`,
      borderRadius: "12px",
      padding: "20px 22px",
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(12px)",
      transition: "opacity 0.5s ease, transform 0.5s ease",
      boxShadow: "0 2px 12px rgba(24,54,94,0.07)",
      flex: "1 1 240px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
        <div style={{ width: "4px", height: "28px", background: card.color, borderRadius: "2px", flexShrink: 0 }} />
        <span style={{ fontSize: "13px", fontWeight: 700, color: T.navy, letterSpacing: "-0.01em" }}>{card.title}</span>
      </div>
      {card.desc && (
        <p style={{ fontSize: "12px", color: T.muted, lineHeight: 1.6, marginBottom: "12px", margin: "0 0 12px 14px" }}>{card.desc}</p>
      )}
      <ul style={{ margin: "0 0 0 14px", padding: 0, listStyle: "none" }}>
        {card.items.map((item, i) => (
          <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "8px" }}>
            <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: card.color, marginTop: "6px", flexShrink: 0 }} />
            <span style={{ fontSize: "12px", color: "#3D3D4E", lineHeight: 1.55 }}>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MilestoneRow({ m, index }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200 + index * 100);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: "14px", padding: "12px 0",
      borderBottom: index < CONFIG.milestones.length - 1 ? `1px solid ${T.border}` : "none",
      opacity: visible ? 1 : 0,
      transform: visible ? "translateX(0)" : "translateX(-10px)",
      transition: "opacity 0.45s ease, transform 0.45s ease",
    }}>
      <div style={{
        width: "32px", height: "32px", borderRadius: "8px",
        background: `linear-gradient(135deg, ${T.navy}, ${T.navyLight})`,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <span style={{ fontSize: "10px", fontWeight: 800, color: T.white, letterSpacing: "0.04em" }}>{m.num}</span>
      </div>
      <div>
        <div style={{ fontSize: "13px", fontWeight: 700, color: T.navy, marginBottom: "3px" }}>{m.label}</div>
        <div style={{ fontSize: "11.5px", color: T.muted, lineHeight: 1.5 }}>{m.detail}</div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function CrossingTheChasmVisualizer() {
  const [view, setView] = useState("market"); // "market" | "monetization"
  const [curveReady, setCurveReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setCurveReady(true), 80);
    return () => clearTimeout(t);
  }, []);

  // SVG dimensions
  const W = 860, H = 280;
  const padX = 20, padTop = 24, padBottom = 60;

  const curvePath = buildCurvePath(W, H, padX, padTop, padBottom);
  const curveAreaPath = curvePath + ` L${W - padX},${H - padBottom} L${padX},${H - padBottom} Z`;

  // Key X positions
  const currentX = pctToX(CONFIG.currentPositionPercent, W, padX);
  const chasmX1  = pctToX(CONFIG.chasmStartPercent, W, padX);
  const chasmX2  = pctToX(CONFIG.chasmEndPercent, W, padX);
  const targetX  = pctToX(52, W, padX); // early majority start

  // Segment label positions
  const segments = [
    { label: "Innovators",      pct: 8,   sub: "~2.5%" },
    { label: "Early Adopters",  pct: 21,  sub: "~13.5%" },
    { label: "⚡ Chasm",        pct: 37.5,sub: "" },
    { label: "Early Majority",  pct: 58,  sub: "~34%" },
    { label: "Late Majority",   pct: 80,  sub: "~34%" },
    { label: "Laggards",        pct: 95,  sub: "~16%" },
  ];

  return (
    <div style={{
      fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
      background: T.bg,
      minHeight: "100vh",
      padding: "32px 24px 48px",
    }}>
      {/* Google Font import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; }
        .tab-btn { transition: all 0.2s ease; cursor: pointer; border: none; }
        .tab-btn:hover { opacity: 0.85; }
        .milestone-hover:hover { background: #f0f4f9 !important; border-radius: 8px; }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{ maxWidth: "920px", margin: "0 auto 28px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
              <div style={{ width: "6px", height: "32px", background: T.coral, borderRadius: "3px" }} />
              <span style={{ fontSize: "11px", fontWeight: 700, color: T.coral, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Market Intelligence · Strategic Intelligence Platform
              </span>
            </div>
            <h1 style={{ margin: "0 0 6px 16px", fontSize: "26px", fontWeight: 900, color: T.navy, letterSpacing: "-0.03em", lineHeight: 1.15 }}>
              Crossing the Chasm
            </h1>
            <p style={{ margin: "0 0 0 16px", fontSize: "13px", color: T.muted, fontWeight: 500 }}>
              Where the SIP category sits — and the path to market leadership
            </p>
          </div>

          {/* View toggle */}
          <div style={{
            display: "flex", background: T.white, borderRadius: "10px",
            border: `1px solid ${T.border}`, padding: "4px", gap: "4px",
            boxShadow: "0 1px 4px rgba(24,54,94,0.08)",
          }}>
            {[
              { key: "market",       label: "Market Stage" },
              { key: "monetization", label: "Monetization" },
            ].map(tab => (
              <button
                key={tab.key}
                className="tab-btn"
                onClick={() => setView(tab.key)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "7px",
                  fontSize: "12px",
                  fontWeight: 700,
                  fontFamily: "inherit",
                  background: view === tab.key ? T.navy : "transparent",
                  color: view === tab.key ? T.white : T.muted,
                  letterSpacing: "0.01em",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── MAIN CARD ── */}
      <div style={{
        maxWidth: "920px", margin: "0 auto",
        background: T.white, borderRadius: "16px",
        border: `1px solid ${T.border}`,
        boxShadow: "0 4px 24px rgba(24,54,94,0.09)",
        overflow: "hidden",
      }}>

        {/* ── CURVE SECTION ── */}
        <div style={{ padding: "28px 28px 0", background: `linear-gradient(180deg, #F8FAFD 0%, ${T.white} 100%)` }}>
          <div style={{ overflowX: "auto" }}>
            <svg
              viewBox={`0 0 ${W} ${H}`}
              style={{ width: "100%", minWidth: "600px", display: "block", opacity: curveReady ? 1 : 0, transition: "opacity 0.6s ease" }}
              aria-label="Technology adoption curve showing Adaptive Product's current market position"
            >
              <defs>
                {/* Curve fill gradient */}
                <linearGradient id="curveGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={T.navyLight} stopOpacity="0.18" />
                  <stop offset="100%" stopColor={T.navyLight} stopOpacity="0.03" />
                </linearGradient>
                {/* Chasm hatch */}
                <pattern id="chasmHatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                  <line x1="0" y1="0" x2="0" y2="6" stroke={T.coral} strokeWidth="1" strokeOpacity="0.25" />
                </pattern>
                {/* Current position glow */}
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                  <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <clipPath id="curveClip">
                  <path d={curveAreaPath} />
                </clipPath>
              </defs>

              {/* Baseline */}
              <line x1={padX} y1={H - padBottom} x2={W - padX} y2={H - padBottom} stroke={T.border} strokeWidth="1.5" />

              {/* Curve fill */}
              <path d={curveAreaPath} fill="url(#curveGrad)" />

              {/* Chasm shaded zone */}
              <rect
                x={chasmX1} y={padTop - 4}
                width={chasmX2 - chasmX1}
                height={H - padBottom - padTop + 4}
                fill="url(#chasmHatch)"
              />
              <rect
                x={chasmX1} y={padTop - 4}
                width={chasmX2 - chasmX1}
                height={H - padBottom - padTop + 4}
                fill={T.coral} fillOpacity="0.06"
              />
              {/* Chasm border lines */}
              <line x1={chasmX1} y1={padTop - 4} x2={chasmX1} y2={H - padBottom} stroke={T.coral} strokeWidth="1.5" strokeDasharray="4 3" strokeOpacity="0.5" />
              <line x1={chasmX2} y1={padTop - 4} x2={chasmX2} y2={H - padBottom} stroke={T.coral} strokeWidth="1.5" strokeDasharray="4 3" strokeOpacity="0.5" />

              {/* Curve stroke */}
              <path d={curvePath} fill="none" stroke={T.navy} strokeWidth="2.5" strokeLinejoin="round" />

              {/* Segment dividers (subtle) */}
              {[CONFIG.chasmStartPercent, CONFIG.chasmEndPercent, 68, 88].map((p, i) => {
                const x = pctToX(p, W, padX);
                return (
                  <line key={i} x1={x} y1={padTop} x2={x} y2={H - padBottom}
                    stroke={T.border} strokeWidth="1" strokeDasharray="3 4" />
                );
              })}

              {/* Target crossing arrow */}
              <g opacity="0.7">
                <line x1={currentX + 10} y1={H - padBottom - 20}
                  x2={targetX - 10} y2={H - padBottom - 20}
                  stroke={T.gold} strokeWidth="1.5" strokeDasharray="5 3" />
                <polygon
                  points={`${targetX - 4},${H - padBottom - 25} ${targetX + 8},${H - padBottom - 20} ${targetX - 4},${H - padBottom - 15}`}
                  fill={T.gold} />
                <text x={(currentX + targetX) / 2} y={H - padBottom - 26}
                  textAnchor="middle" fill={T.gold}
                  fontSize="9" fontWeight="700" fontFamily="Montserrat" letterSpacing="0.08em">
                  TARGET CROSSING ZONE
                </text>
              </g>

              {/* Current position marker */}
              <g filter="url(#glow)">
                {/* Vertical drop line */}
                <line x1={currentX} y1={padTop + 10} x2={currentX} y2={H - padBottom}
                  stroke={T.coral} strokeWidth="1.5" strokeDasharray="5 3" />
                {/* Dot on curve — approximate Y at currentPositionPercent */}
                {(() => {
                  const t = CONFIG.currentPositionPercent / 100;
                  const norm = gaussY(t, 0.62, 0.18, 1);
                  const cy = padTop + (H - padTop - padBottom) * (1 - norm * 0.88);
                  return (
                    <g>
                      <circle cx={currentX} cy={cy} r="7" fill={T.coral} />
                      <circle cx={currentX} cy={cy} r="11" fill={T.coral} fillOpacity="0.2" />
                      <circle cx={currentX} cy={cy} r="3" fill={T.white} />
                    </g>
                  );
                })()}
              </g>

              {/* Current position label */}
              <g>
                <rect x={currentX - 120} y={padTop - 20} width="240" height="26" rx="5"
                  fill={T.navy} />
                <text x={currentX} y={padTop - 3} textAnchor="middle"
                  fill={T.white} fontSize="9.5" fontWeight="800" fontFamily="Montserrat" letterSpacing="0.04em">
                  ADAPTIVE PRODUCT · SIP POSITION
                </text>
              </g>

              {/* Segment labels */}
              {segments.map((seg, i) => {
                const x = pctToX(seg.pct, W, padX);
                const isChasm = seg.label.includes("Chasm");
                return (
                  <g key={i}>
                    <text x={x} y={H - padBottom + 16}
                      textAnchor="middle" fill={isChasm ? T.coral : T.muted}
                      fontSize={isChasm ? "10" : "9.5"} fontWeight={isChasm ? "800" : "600"}
                      fontFamily="Montserrat" letterSpacing="0.04em">
                      {seg.label}
                    </text>
                    {seg.sub && (
                      <text x={x} y={H - padBottom + 28}
                        textAnchor="middle" fill={T.border}
                        fontSize="8" fontWeight="500" fontFamily="Montserrat">
                        {seg.sub}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Chasm label overlay */}
              <text x={(chasmX1 + chasmX2) / 2} y={H - padBottom - 8}
                textAnchor="middle" fill={T.coral}
                fontSize="8.5" fontWeight="700" fontFamily="Montserrat" letterSpacing="0.1em">
                THE CHASM
              </text>
            </svg>
          </div>

          {/* Stage pill */}
          <div style={{ display: "flex", justifyContent: "center", paddingBottom: "20px" }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "8px",
              background: `${T.coral}12`, border: `1px solid ${T.coral}30`,
              borderRadius: "99px", padding: "6px 16px",
            }}>
              <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: T.coral }} />
              <span style={{ fontSize: "11.5px", fontWeight: 700, color: T.coral, letterSpacing: "0.04em" }}>
                Current Market Position: {CONFIG.stageLabel}
              </span>
            </div>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div style={{ height: "1px", background: T.border }} />

        {/* ── BOTTOM PANEL — Market Stage View ── */}
        {view === "market" && (
          <div style={{ padding: "24px 28px 28px" }}>

            {/* Cards row */}
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
              {MARKET_CARDS.map((card, i) => <InfoCard key={card.id} card={card} index={i} />)}
            </div>

            {/* Milestones — full width now that probability section is removed */}
            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
              <div style={{
                flex: "1 1 100%", background: T.bg, borderRadius: "12px",
                border: `1px solid ${T.border}`, padding: "20px 22px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                  <div style={{ width: "4px", height: "20px", background: T.gold, borderRadius: "2px" }} />
                  <span style={{ fontSize: "12px", fontWeight: 700, color: T.navy, letterSpacing: "-0.01em" }}>Milestones to Cross the Chasm</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "0 32px" }}>
                  {CONFIG.milestones.map((m, i) => <MilestoneRow key={m.num} m={m} index={i} />)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── BOTTOM PANEL — Monetization View ── */}
        {view === "monetization" && (
          <div style={{ padding: "24px 28px 28px" }}>

            {/* Wedge label */}
            <div style={{
              background: `${T.navy}08`, border: `1px solid ${T.navy}18`,
              borderRadius: "10px", padding: "14px 18px", marginBottom: "20px",
              display: "flex", alignItems: "flex-start", gap: "12px",
            }}>
              <div style={{ width: "4px", height: "100%", background: T.coral, borderRadius: "2px", alignSelf: "stretch", minHeight: "20px" }} />
              <div>
                <div style={{ fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>Market Wedge</div>
                <div style={{ fontSize: "13px", fontWeight: 700, color: T.navy }}>{CONFIG.wedgeLabel}</div>
              </div>
            </div>

            {/* Monetization cards */}
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "20px" }}>
              {MONETIZATION_CARDS.map((card, i) => <InfoCard key={card.id} card={card} index={i} />)}
            </div>

            {/* Services → Platform flywheel */}
            <div style={{
              background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyLight} 100%)`,
              borderRadius: "12px", padding: "22px 24px",
              display: "flex", alignItems: "center", gap: "0", flexWrap: "wrap",
            }}>
              {[
                { icon: "🤝", label: "Structured Engagement", sub: "Trust + cash flow" },
                { icon: "→", label: "", sub: "", arrow: true },
                { icon: "📊", label: "Proof Generation", sub: "CFO-grade evidence" },
                { icon: "→", label: "", sub: "", arrow: true },
                { icon: "🏛️", label: "Platform Adoption", sub: "Recurring SaaS revenue" },
                { icon: "→", label: "", sub: "", arrow: true },
                { icon: "🏆", label: "Category Leadership", sub: "Decision Infrastructure" },
              ].map((step, i) => step.arrow ? (
                <div key={i} style={{ flex: "0 0 auto", padding: "0 8px", color: T.coral, fontSize: "18px", fontWeight: 700 }}>→</div>
              ) : (
                <div key={i} style={{ flex: "1 1 120px", textAlign: "center", padding: "4px 8px" }}>
                  <div style={{ fontSize: "20px", marginBottom: "6px" }}>{step.icon}</div>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: T.white, marginBottom: "3px" }}>{step.label}</div>
                  <div style={{ fontSize: "10px", color: `${T.white}80` }}>{step.sub}</div>
                </div>
              ))}
            </div>

          </div>
        )}

        {/* ── FOOTER ── */}
        <div style={{
          borderTop: `1px solid ${T.border}`,
          padding: "12px 28px",
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px",
          background: T.bg,
        }}>
          <span style={{ fontSize: "10px", color: T.muted, letterSpacing: "0.04em" }}>
            ADAPTIVE PRODUCT · STRATEGY INTELLIGENCE PLATFORM · CONFIDENTIAL
          </span>
          <span style={{ fontSize: "10px", color: T.muted }}>
            Decision Infrastructure for Health Systems
          </span>
        </div>
      </div>

      {/* ── WEDGE CALLOUT (always visible below) ── */}
      <div style={{ maxWidth: "920px", margin: "16px auto 0", textAlign: "center" }}>
        <span style={{ fontSize: "11px", color: T.muted, fontStyle: "italic" }}>
          "Replace Opinion with Evidence — in the Decisions That Matter Most."
        </span>
      </div>
    </div>
  );
}
